import { Modal, Text, Box, Center, Loader, Image } from '@mantine/core';
import { useStore } from '@/store';
import { useUserProfile, useBrRank, useCsRank } from '@/models/User';
import { getRareById, getDisplayName } from '@/models/Metadata';
import { translate } from '@/lib';
import style from './Style.module.scss';
import clsx from 'clsx';

export function ProfileModal() {
  const { profileModalOpen, updateState } = useStore();

  // Fetch profile data when modal is open
  const { data: profile, isLoading } = useUserProfile(profileModalOpen || false);

  // Fetch BR and CS rank details
  const { data: brRankData } = useBrRank(profile?.brRank, profileModalOpen || false);
  const { data: csRankData } = useCsRank(profile?.csRank, profileModalOpen || false);

  const handleClose = () => {
    updateState('profileModalOpen', false);
  };

  // Map collections keeping original order from profile.collections
  const getCollections = () => {
    if (!profile?.collections) return [];

    return profile.collections.map(c => {
      const rare = getRareById(c.id);
      return {
        id: c.id,
        label: rare ? getDisplayName(rare) : `#${c.id}`,
        count: c.quantity,
      };
    });
  };

  const collections = getCollections();

  return (
    <Modal
      opened={profileModalOpen || false}
      onClose={handleClose}
      title={
        <Text fw={700} size="lg">
          {translate('ff.profile.title', 'Profile')}
        </Text>
      }
      size="md"
      centered
    >
      <Box className={style.container}>
        {isLoading ? (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        ) : profile ? (
          <>
            {/* Prime Level Section */}
            {profile.primeLevel > 0 && (
              <div className={style.section}>
                <Text className={style.sectionTitle}>
                  {translate('ff.profile.primeLevel', 'Prime Level')}
                </Text>
                <div className={style.primeLevel}>
                  <span className={style.primeBadge}>{profile.primeLevel}</span>
                </div>
              </div>
            )}

            {/* Collections Section - only show if there's data */}
            {collections.length > 0 && collections.some(c => c.count > 0) && (
              <div className={style.section}>
                <Text className={style.sectionTitle}>
                  {translate('ff.profile.collections', 'Collections')}
                </Text>
                <div className={style.collectionsGrid}>
                  {collections.filter(c => c.count > 0).map((item) => (
                    <div key={item.id} className={`${style.collectionCard} tier-bg-${item.id}`}>
                      <div className={style.collectionCount}>
                        {item.count}
                      </div>
                      <div className={style.collectionLabel}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Season Section */}
            <div className={clsx(style.section, style['section-none'])}>
              <Text className={style.sectionTitle}>
                {translate('ff.profile.currentSeason', 'Current Season')}
              </Text>
              <div className={style.ranksGrid}>
                {/* BR Rank Card */}
                <div className={style.rankCard}>
                  <div className={style.rankMode}>BR</div>
                  <div className={style.rankContent}>
                    {brRankData?.icon && (
                      <div className={style.rankIconWrapper}>
                        <Image
                          src={brRankData.icon}
                          alt={brRankData.name || 'BR Rank'}
                          width={48}
                          height={48}
                          fit="contain"
                        />
                      </div>
                    )}
                    <div className={style.rankName}>
                      {brRankData?.name || '-'}
                    </div>
                  </div>
                </div>

                {/* CS Rank Card */}
                <div className={style.rankCard}>
                  <div className={style.rankMode}>CS</div>
                  <div className={style.rankContent}>
                    {csRankData?.icon && (
                      <div className={style.rankIconWrapper}>
                        <Image
                          src={csRankData.icon}
                          alt={csRankData.name || 'CS Rank'}
                          width={48}
                          height={48}
                          fit="contain"
                        />
                      </div>
                    )}
                    <div className={style.rankName}>
                      {csRankData?.name || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <Center py="xl">
            <Text c="dimmed">{translate('ff.profile.noData', 'No profile data available')}</Text>
          </Center>
        )}
      </Box>
    </Modal>
  );
}
